import { SEO } from "@/components/common/seo";

const PolicySection = ({
  id,
  title,
  children,
}: {
  id?: string;
  title: string;
  children: React.ReactNode;
}) => (
  <section id={id} className="mb-10 scroll-mt-24">
    <h2 className="font-display text-xl font-semibold text-foreground mb-4 border-b border-border pb-2">
      {title}
    </h2>
    <div className="text-foreground/85 text-[16px] leading-relaxed space-y-3">
      {children}
    </div>
  </section>
);

const PrivacyPolicy = () => {
  return (
    <>
      <SEO canonical="/privacy-policy" />
      <div className="min-h-screen bg-background" id="top">
        <div className="w-full max-w-7xl xl:max-w-[80rem] 2xl:max-w-[88rem] mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 py-10 sm:py-12 md:py-16">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] xl:grid-cols-[1fr_300px] 2xl:grid-cols-[1fr_320px] gap-8 md:gap-10 lg:gap-12 xl:gap-16">
            <article className="min-w-0 order-2 lg:order-1">
              <header className="mb-10 flex flex-col items-center justify-center gap-1">
                <h1 className="font-display text-3xl md:text-5xl font-bold text-foreground tracking-tight">
                  Privacy Policy
                </h1>
                <p className="text-muted-foreground text-sm mt-2">
                  Last updated: February 11, 2026
                </p>
              </header>

              <p className="text-foreground/85 text-[16px] leading-relaxed mb-10">
                By utilizing our Service, you agree to the collection and use of information in accordance with this refined Privacy Policy to ensure a personalized and secure design experience.
              </p>

              <PolicySection id="interpretation-and-definitions" title="Interpretation and Definitions">
                <p className="text-foreground/85 text-[16px] leading-relaxed">
                  Capitalized terms used in this document have specific meanings defined below, applicable whether they appear in singular or plural form.
                </p>
                <dl className="mt-4 space-y-3">
                  <div>
                    <dt className="font-medium text-foreground">Company</dt>
                    <dd className="ml-4 text-foreground/80 text-[16px] leading-relaxed">
                      Refers to Somatiq AI Labs Private Limited, No. 07/3, Second Floor, 18th Main Rd, Jayanagara 9th Block, Bengaluru, Karnataka 560041.
                    </dd>
                  </div>
                  <div>
                    <dt className="font-medium text-foreground">Service</dt>
                    <dd className="ml-4 text-foreground/80 text-[16px] leading-relaxed">
                      Refers to the Somatiq AI Website and the proprietary AI architectural visualization tools and consultations provided through it.
                    </dd>
                  </div>
                  <div>
                    <dt className="font-medium text-foreground">Personal Data</dt>
                    <dd className="ml-4 text-foreground/80 text-[16px] leading-relaxed">
                      Any information that identifies or can be used to identify an individual, including professional and project-related details.
                    </dd>
                  </div>
                  <div>
                    <dt className="font-medium text-foreground">Usage Data</dt>
                    <dd className="ml-4 text-foreground/80 text-[16px] leading-relaxed">
                      Refers to data collected automatically, such as page visit duration, navigation paths through design portfolios, and technical diagnostic data.
                    </dd>
                  </div>
                  <div>
                    <dt className="font-medium text-foreground">You</dt>
                    <dd className="ml-4 text-foreground/80 text-[16px] leading-relaxed">
                      The individual or legal entity accessing the Service for architectural, design, or visualization purposes.
                    </dd>
                  </div>
                </dl>
              </PolicySection>

              <PolicySection id="collecting-and-using-personal-data" title="Collecting and Using Your Personal Data">
                <h3 className="font-display font-medium text-foreground mt-4 mb-2">Types of Data Collected</h3>
                <p className="mb-2">To provide bespoke AI design solutions, we may ask for:</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li className="text-foreground/85 text-[16px] leading-relaxed">Identity Info: Full name and professional title</li>
                  <li className="text-foreground/85 text-[16px] leading-relaxed">Contact Info: Email address and phone number</li>
                  <li className="text-foreground/85 text-[16px] leading-relaxed">Project Info: Site address, ZIP/Postal code, and project requirements</li>
                  <li className="text-foreground/85 text-[16px] leading-relaxed">Interaction Info: Preferences for specific architectural styles or renderings</li>
                </ul>

                <h3 className="font-display font-medium text-foreground mt-6 mb-2">Usage Data</h3>
                <ul className="list-disc pl-5 space-y-1">
                  <li className="text-foreground/85 text-[16px] leading-relaxed">Collected automatically via our AI interface.</li>
                  <li className="text-foreground/85 text-[16px] leading-relaxed">Includes IP addresses, browser versions, and specific design modules or pages you interact with most.</li>
                  <li className="text-foreground/85 text-[16px] leading-relaxed">Mobile access data includes device unique IDs, operating systems, and mobile network information.</li>
                </ul>

                <h3 className="font-display font-medium text-foreground mt-6 mb-2">Tracking Technologies</h3>
                <p className="mb-2">We utilize modern tracking to enhance your design experience:</p>
                <ul className="list-none space-y-2">
                  <li>
                    <span className="font-medium text-foreground">Session Cookies:</span>{" "}
                    <span className="text-foreground/80 text-[16px] leading-relaxed">Essential for navigating our AI design dashboard and maintaining secure sessions.</span>
                  </li>
                  <li>
                    <span className="font-medium text-foreground">Preference Cookies:</span>{" "}
                    <span className="text-foreground/80 text-[16px] leading-relaxed">To remember your architectural preferences and regional settings for a tailored experience.</span>
                  </li>
                  <li>
                    <span className="font-medium text-foreground">Web Beacons:</span>{" "}
                    <span className="text-foreground/80 text-[16px] leading-relaxed">To analyze the engagement levels of our digital design portfolios and email updates.</span>
                  </li>
                </ul>

                <h3 className="font-display font-medium text-foreground mt-6 mb-2">Use of Personal Data</h3>
                <ul className="list-none space-y-2">
                  <li>
                    <span className="font-medium text-foreground">Service Delivery:</span>{" "}
                    <span className="text-foreground/80 text-[16px] leading-relaxed">Generating AI architectural visualizations and managing project-specific consultations.</span>
                  </li>
                  <li>
                    <span className="font-medium text-foreground">Communication:</span>{" "}
                    <span className="text-foreground/80 text-[16px] leading-relaxed">Sending updates on project milestones, security alerts, and administrative changes.</span>
                  </li>
                  <li>
                    <span className="font-medium text-foreground">Product Innovation:</span>{" "}
                    <span className="text-foreground/80 text-[16px] leading-relaxed">Analyzing usage trends to improve our AI models, rendering speed, and user interface.</span>
                  </li>
                  <li>
                    <span className="font-medium text-foreground">Marketing & News:</span>{" "}
                    <span className="text-foreground/80 text-[16px] leading-relaxed">Sharing insights on new AI capabilities or design events, subject to your opt-out rights.</span>
                  </li>
                </ul>

                <h3 className="font-display font-medium text-foreground mt-6 mb-2">Data Retention and Transfer</h3>
                <p className="text-foreground/85 text-[16px] leading-relaxed">
                  <span className="font-medium text-foreground">Retention: </span>
                  We retain data only as long as necessary for project fulfillment, legal compliance, or to resolve design-related disputes.
                </p>
                <p>
                  <span className="font-medium text-foreground">Transfer: </span>
                  Data may be processed at our Bengaluru offices or via secure cloud infrastructure globally. We ensure all transfers meet high security standards and Indian data protection laws.
                </p>

                <h3 className="font-display font-medium text-foreground mt-6 mb-2">Security</h3>
                <p className="text-foreground/85 text-[16px] leading-relaxed">
                  While we employ industry-leading encryption to protect your architectural assets, no electronic transmission is 100% secure; we strive for maximum commercially acceptable protection.
                </p>
              </PolicySection>

              <PolicySection id="your-rights-and-compliance" title="Your Rights and Compliance">
                <h3 className="font-display font-medium text-foreground mt-2 mb-2">Your Rights</h3>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Access and update personal information via account settings.</li>
                  <li>Request deletion of data no longer required for legal or project purposes.</li>
                  <li>Withdraw consent for marketing communications at any time.</li>
                </ul>
                <p className="mt-4">
                  <span className="font-medium text-foreground">Children’s Privacy: </span>
                  Our services are professional-grade and not intended for individuals under 13 years of age.
                </p>
                <p className="mt-2">
                  <span className="font-medium text-foreground">Third-Party Links: </span>
                  We are not responsible for the privacy practices of external sites linked within our design resources.
                </p>
              </PolicySection>

              <PolicySection id="contact-us" title="Contact Us">
                <p className="mb-3">You can reach us via:</p>
                <ul className="list-none space-y-2">
                  <li>
                    <span className="font-medium text-foreground">Email: </span>
                    <a href="mailto:contact@somatiq.ai" className="text-(--brilliant-azure) hover:underline">
                      contact@somatiq.ai
                    </a>
                  </li>
                </ul>
              </PolicySection>
            </article>

            <aside className="lg:sticky lg:top-24 lg:self-start order-1 lg:order-2">
              <nav className="px-6 py-5 rounded-lg border border-border bg-background min-w-0" aria-label="Table of contents">
                <h2 className="font-display font-bold text-foreground text-base mb-4">
                  Table of contents
                </h2>
                <ol className="space-y-3 text-[15px] list-none">
                  <li className="flex gap-2">
                    <span className="shrink-0 text-foreground/70">1.</span>
                    <a href="#interpretation-and-definitions" className="toc-link">
                      Interpretation and Definitions
                    </a>
                  </li>
                  <li className="flex gap-2">
                    <span className="shrink-0 text-foreground/70">2.</span>
                    <a href="#collecting-and-using-personal-data" className="toc-link">
                      Collecting and Using Your Personal Data
                    </a>
                  </li>
                  <li className="flex gap-2">
                    <span className="shrink-0 text-foreground/70">3.</span>
                    <a href="#your-rights-and-compliance" className="toc-link">
                      Your Rights and Compliance
                    </a>
                  </li>
                  <li className="flex gap-2">
                    <span className="shrink-0 text-foreground/70">4.</span>
                    <a href="#contact-us" className="toc-link">
                      Contact Us
                    </a>
                  </li>
                </ol>
                <p className="mt-5 pt-4 border-t border-border">
                  <a href="#top" className="toc-link font-semibold inline-flex items-center gap-1">
                    Back to top ↑
                  </a>
                </p>
              </nav>
            </aside>
          </div>
        </div>
      </div>
    </>
  );
};

export default PrivacyPolicy;
